import * as C from 'cesium';
import { mapStyleProvider } from '@/features/loader/globe/layers';

export const GOOGLE_SATELLITE_ASSET = 3830182;
export interface SiteImagery {
  provider: C.ImageryProvider;
  source: 'Google Maps' | 'Esri';
}
let imagery: Promise<SiteImagery> | undefined;

/** Share the official provider/session between the ground and photographic roofs.
 * Google tiles stream at runtime; they are never bundled or stored for offline use. */
export function siteImagery(): Promise<SiteImagery> {
  return imagery ??= (async () => {
    const token = import.meta.env.VITE_CESIUM_ION_TOKEN;
    if (token) {
      try {
        const provider = await C.IonImageryProvider.fromAssetId(GOOGLE_SATELLITE_ASSET, {accessToken: token});
        // Initialize the SDK's viewport credits once before ground and roof
        // requests fan out; otherwise each first tile repeats that metadata load.
        const firstTile = await provider.requestImage(0, 0, 0);
        (firstTile as {close?: () => void} | undefined)?.close?.();
        return {provider, source: 'Google Maps' as const};
      } catch { /* A missing entitlement must not remove the existing site map. */ }
    }
    return {provider: mapStyleProvider('satellite'), source: 'Esri' as const};
  })();
}

// Roofs can remain visible above the globe's ground tiles. Retain their source
// credits in Cesium's normal attribution display until the roof view is disposed.
const roofCredits = new Map<symbol, C.Credit[]>();
const listeners = new Set<(credits: C.Credit[]) => void>();
const credits = () => [...new Map([...roofCredits.values()].flat().map(c => [c.html, c])).values()];
export function setRoofCredits(owner: symbol, values: C.Credit[]) {
  if (values.length) roofCredits.set(owner, values); else roofCredits.delete(owner);
  const current = credits();
  listeners.forEach(listener => listener(current));
}
export function observeRoofCredits(listener: (credits: C.Credit[]) => void): () => void {
  listeners.add(listener);
  listener(credits());
  return () => { listeners.delete(listener); };
}
